import React, { useEffect, useRef, useCallback, useMemo } from 'react'
import FilterForm from './FilterForm'
import { useCrossStatisticsForm, useCrossStatisticsApi } from './hooks'
import useDebounceCallback from '../../hooks/useDebounceCallback'
import GradeTable from './GradeTable'
import NumericTable from './NumericTable'
import SelectionTable from './SelectionTable'
import {
  SELECTION_DISPLAY_OPTIONS,
  COMPARE_TYPES,
  DEBOUNCE_DELAY,
  DISPLAY_MODES,
  GRADE_DISPLAY_OPTIONS,
  SAVE_PREFERENCES_DELAY
} from './constants'
import EmptyState from '../ui/EmptyState'
import CrossStatisticsService from '../../services/api/CrossStatisticsService'
import { formatDateParam } from '../../utils/dateHelpers'

const findOption = (options, id) => options.find(opt => String(opt.value) === String(id)) || null

const buildUrlParams = ({ variableId, questionIds, startDate, endDate, displayMode, compareType }) => {
  const params = new URLSearchParams()
  params.set('variable_id', variableId)
  if (compareType === COMPARE_TYPES.GRADE) {
    params.set('statistics_type', COMPARE_TYPES.GRADE)
    questionIds.forEach(id => params.append('question_ids[]', id))
  } else {
    params.set('question_id', questionIds[0])
    if (compareType === COMPARE_TYPES.NUMERIC)
      params.set('statistics_type', COMPARE_TYPES.NUMERIC)
  }
  if (startDate)
    params.set('start_date', formatDateParam(startDate))
  if (endDate)
    params.set('end_date', formatDateParam(endDate))
  if (displayMode !== DISPLAY_MODES.COUNT)
    params.set('display_mode', displayMode)
  return params
}

const CrossStatistics = ({
  variables,
  sections,
  formUrl,
  initialVariableId,
  initialQuestionId,
  initialQuestionIds,
  initialStartDate,
  initialEndDate,
  initialDisplayMode,
  initialMode,
  initialStatistics,
  filterResourcesUrl,
  exportUrl,
  hasSelectionQuestions = true,
  savePreferencesUrl,
  hasGradeQuestions = false,
  hasNumericQuestions = false,
  showPeriodFilter = true
}) => {
  const isInitialMount = useRef(true)
  const isInitialSave = useRef(true)

  const defaultCompareType = useMemo(() => {
    if (initialMode)
      return initialMode
    if (hasSelectionQuestions)
      return COMPARE_TYPES.SELECTION
    if (hasGradeQuestions)
      return COMPARE_TYPES.GRADE
    return COMPARE_TYPES.NUMERIC
  }, [])

  const {
    formState,
    updateField,
    setDateRange,
    switchCompareType
  } = useCrossStatisticsForm({
    variableId: initialVariableId || '',
    questionId: initialQuestionId || '',
    questionIds: initialQuestionIds || [],
    startDate: initialStartDate || null,
    endDate: initialEndDate || null,
    displayMode: initialDisplayMode || DISPLAY_MODES.COUNT,
    compareType: defaultCompareType
  })

  const {
    statistics,
    loading,
    fetchError,
    fetchStatistics,
    resetStatistics
  } = useCrossStatisticsApi(formUrl, formState, initialStatistics)

  const isGrade = formState.compareType === COMPARE_TYPES.GRADE
  const isNumeric = formState.compareType === COMPARE_TYPES.NUMERIC

  const displayOptions = isGrade ? GRADE_DISPLAY_OPTIONS : SELECTION_DISPLAY_OPTIONS

  // Only re-fetch when API-relevant params change (not displayMode or questionIds in grade)
  const fetchKey = useMemo(() => {
    const { variableId, questionIds, startDate, endDate, compareType } = formState
    const questionParam = compareType === COMPARE_TYPES.GRADE ? '' : String(questionIds[0] || '')
    return [variableId, questionParam, startDate, endDate, compareType].join('|')
  }, [formState])

  const debouncedFetch = useDebounceCallback(fetchStatistics, DEBOUNCE_DELAY)

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    debouncedFetch()
  }, [fetchKey, debouncedFetch])

  // Sync URL with form state when statistics have been loaded at least once
  useEffect(() => {
    if (!statistics && !fetchError)
      return
    const params = buildUrlParams(formState)
    window.history.replaceState(null, '', `${window.location.pathname}?${params}`)
  }, [formState, statistics, fetchError])

  const savePreferences = useDebounceCallback(async() => {
    if (!savePreferencesUrl)
      return
    try {
      await CrossStatisticsService.savePreferences(savePreferencesUrl, formState)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Erreur lors de la sauvegarde des préférences du comparatif', error)
    }
  }, SAVE_PREFERENCES_DELAY)

  useEffect(() => {
    if (isInitialSave.current) {
      isInitialSave.current = false
      return
    }
    savePreferences()
  }, [formState, savePreferences])

  // Transform options for select components
  const variableOptions = useMemo(
    () => variables.map(variable => ({ value: variable.id, label: variable.name })), [variables]
  )

  const buildGroupedOptions = useCallback(contextFilter => sections
    .map(section => {
      const filtered = (section.questions || []).filter(contextFilter)
      if (filtered.length === 0)
        return null
      return {
        label: section.name,
        options: filtered.map(question => ({ value: question.id, label: question.title }))
      }
    })
    .filter(Boolean), [sections])

  const isSelectionContext = useCallback(
    question => ['selection', 'selection_multi'].includes(question.context), []
  )
  const questionOptions = useMemo(() => buildGroupedOptions(isSelectionContext), [
    buildGroupedOptions, isSelectionContext
  ])
  const gradeQuestionOptions = useMemo(() => {
    const questionGroups = buildGroupedOptions(question => question.context === 'grade')
    if (questionGroups.length === 0)
      return []

    const globalOptions = [{ value: 'collect', label: 'Collecte' }]
    const sectionOptions = []
    sections.forEach(section => {
      if (section.questions?.some(question => question.context === 'grade'))
        sectionOptions.push({ value: `section_${section.id}`, label: section.name })
    })

    const groups = [{ label: 'Globales', options: globalOptions }]
    if (sectionOptions.length > 0)
      groups.push({ label: 'Sections', options: sectionOptions })
    return [...groups, ...questionGroups]
  }, [buildGroupedOptions, sections])
  const numericQuestionOptions = useMemo(
    () => buildGroupedOptions(question => question.context === COMPARE_TYPES.NUMERIC), [buildGroupedOptions]
  )

  const selectedVariable = useMemo(
    () => findOption(variableOptions, formState.variableId), [variableOptions, formState.variableId]
  )

  const selectedQuestions = useMemo(() => {
    let options = questionOptions
    if (isGrade)
      options = gradeQuestionOptions
    else if (isNumeric)
      options = numericQuestionOptions
    const allOptions = options.flatMap(group => group.options)
    return allOptions.filter(opt => formState.questionIds.includes(opt.value))
  }, [
    formState.questionIds,
    isGrade,
    isNumeric,
    questionOptions,
    gradeQuestionOptions,
    numericQuestionOptions
  ])

  // Handlers
  const handleVariableChange = useCallback(
    option => updateField('variableId', option?.value || ''), [updateField]
  )

  const handleQuestionChange = useCallback(
    option => updateField('questionIds', option ? [option.value] : []), [updateField]
  )

  const handleQuestionsChange = useCallback(
    selected => updateField('questionIds', (selected || []).map(opt => opt.value)), [updateField]
  )

  const handleCompareTypeChange = useCallback(option => {
    resetStatistics()
    switchCompareType(option?.value || COMPARE_TYPES.SELECTION)
  }, [resetStatistics, switchCompareType])

  const handleDisplayChange = useCallback(
    option => updateField('displayMode', option?.value || DISPLAY_MODES.COUNT), [updateField]
  )

  // Filter statistics by mode and selected question IDs
  const filteredStatistics = useMemo(() => {
    if (!statistics)
      return null

    if (isGrade) {
      if (formState.questionIds.length === 0)
        return null

      const selectedIds = new Set(formState.questionIds)
      return {
        ...statistics,
        columns: statistics.columns.filter(column => selectedIds.has(column.id))
      }
    }

    // Numeric mode returns rows + totalSum
    if (isNumeric)
      return statistics.rows ? statistics : null

    // Selection mode requires totals (guard against grade data during mode switch)
    return statistics.totals ? statistics : null
  }, [statistics, isGrade, isNumeric, formState.questionIds])

  const exportHref = useMemo(() => {
    if (!filteredStatistics || !exportUrl)
      return null
    return `${exportUrl}?${buildUrlParams(formState)}`
  }, [exportUrl, filteredStatistics, formState])

  const tableContent = useMemo(() => {
    if (loading)
      return <div className="text-center py-4"><i className="fas fa-spinner fa-spin fa-2x text-muted" /></div>

    if (fetchError)
      return <div className="alert alert-danger">{fetchError}</div>

    if (!filteredStatistics) {
      return (
        <EmptyState
          icon="fa-table"
          message="Sélectionnez une variable et une question pour afficher les statistiques."
        />
      )
    }

    if (isGrade) {
      return (
        <GradeTable
          displayMode={formState.displayMode}
          filterUrl={filterResourcesUrl}
          statistics={filteredStatistics}
          variableId={formState.variableId}
        />
      )
    }

    if (isNumeric) {
      return (
        <NumericTable
          filterUrl={filterResourcesUrl}
          statistics={filteredStatistics}
          variableId={formState.variableId}
        />
      )
    }

    return (
      <SelectionTable
        displayMode={formState.displayMode}
        filterUrl={filterResourcesUrl}
        statistics={filteredStatistics}
        variableId={formState.variableId}
      />
    )
  }, [
    fetchError,
    filterResourcesUrl,
    filteredStatistics,
    formState.displayMode,
    formState.variableId,
    isGrade,
    isNumeric,
    loading
  ])

  return (
    <div className="card">
      <div className="card-header bg-light">
        <FilterForm
          compareType={formState.compareType}
          displayMode={formState.displayMode}
          displayOptions={displayOptions}
          hasSelectionQuestions={hasSelectionQuestions}
          hasGradeQuestions={hasGradeQuestions}
          hasNumericQuestions={hasNumericQuestions}
          initialEndDate={initialEndDate}
          initialStartDate={initialStartDate}
          gradeQuestionOptions={gradeQuestionOptions}
          numericQuestionOptions={numericQuestionOptions}
          questionOptions={questionOptions}
          selectedQuestions={selectedQuestions}
          selectedVariable={selectedVariable}
          showPeriodFilter={showPeriodFilter}
          variableOptions={variableOptions}
          onDateRangeChange={setDateRange}
          onCompareTypeChange={handleCompareTypeChange}
          onDisplayChange={handleDisplayChange}
          onQuestionChange={handleQuestionChange}
          onQuestionsChange={handleQuestionsChange}
          onVariableChange={handleVariableChange}
        />
      </div>
      <div className="card-body">
        {exportHref ? (
          <div className="text-right mb-3">
            <a className="btn btn-primary" href={exportHref}>
              <i className="fas fa-file-export mr-1" />
              <span>Exporter</span>
            </a>
          </div>
        ) : null}
        {tableContent}
      </div>
    </div>
  )
}

export default CrossStatistics
